/**
 * DocumentUploadSummary — what a document upload request proposes, in words (P23, §3j),
 * shown on the change-request page instead of the raw field table.
 *
 * The approver opens the file before deciding (Pedro 2026-10-10: the approval screen
 * shows the pending file). After a rejection or a cancellation the bytes are gone;
 * the name, size and SHA-256 stay on the request as the record of what was refused.
 */
import { Alert, Button, Code, Image, Table, Text } from '@mantine/core';
import { IconEye } from '@tabler/icons-react';

import { documentsApi, formatFileSize, type DocumentOwnerType } from '../api/documents';
import type { ChangeRequest } from '../api/types';
import { useDocumentKinds } from '../hooks/useDocuments';
import { EntityFriendlyLabel } from './EntityFriendlyLabel';

export function DocumentUploadSummary({ cr, canOpenPending }: {
  cr: ChangeRequest;
  /** The requester and the approvers may open a pending file (the server enforces it). */
  canOpenPending: boolean;
}) {
  const kindsQ = useDocumentKinds();
  const p = (cr.payload ?? {}) as Record<string, unknown>;
  const kind = String(p.kind ?? '');
  const kindLabel = kindsQ.data?.find((k) => k.kind === kind)?.label ?? kind;
  const ownerType = String(p.owner_type ?? '') as DocumentOwnerType;
  const ownerId = typeof p.owner_id === 'string' ? p.owner_id : null;
  const gone = cr.status === 'REJECTED' || cr.status === 'CANCELLED';
  // P24: a person photo shows as a thumbnail, so the approver sees it at a glance.
  const thumb = kind !== 'PHOTO' ? null
    : cr.status === 'PENDING' && canOpenPending ? `/documents/pending/${cr.id}/file?inline=true`
      : cr.status === 'APPLIED' && cr.entity_id ? `/documents/${cr.entity_id}/file?inline=true`
        : null;

  return (
    <>
      {thumb && (
        <Image src={thumb} alt="The photo in this request" h={160} w="auto" fit="contain"
          radius="sm" mb="sm" style={{ border: '1px solid var(--mantine-color-gray-3)' }} />
      )}
      <Table withTableBorder>
        <Table.Tbody>
          <Table.Tr>
            <Table.Th w={180}>Document type</Table.Th>
            <Table.Td>{kindLabel}</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Th>Attached to</Table.Th>
            <Table.Td><EntityFriendlyLabel entityType={ownerType} entityId={ownerId} /></Table.Td>
          </Table.Tr>
          {typeof p.title === 'string' && p.title && (
            <Table.Tr><Table.Th>Title</Table.Th><Table.Td>{p.title}</Table.Td></Table.Tr>
          )}
          {typeof p.notes === 'string' && p.notes && (
            <Table.Tr><Table.Th>Notes</Table.Th><Table.Td>{p.notes}</Table.Td></Table.Tr>
          )}
          <Table.Tr>
            <Table.Th>File</Table.Th>
            <Table.Td>
              {String(p.filename ?? '')}
              <Text c="dimmed" size="xs" component="span">
                {' '}· {formatFileSize(typeof p.size_bytes === 'number' ? p.size_bytes : null)}
                {p.mime_type ? ` · ${String(p.mime_type)}` : ''}
              </Text>
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Th>SHA-256</Table.Th>
            <Table.Td><Code style={{ fontSize: 11 }}>{String(p.sha256 ?? '')}</Code></Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
      {cr.status === 'PENDING' && canOpenPending && (
        <Button mt="sm" size="xs" variant="light" leftSection={<IconEye size={14} />}
          onClick={() => documentsApi.viewPending(cr.id)}>
          View the file (opens a new tab)
        </Button>
      )}
      {cr.status === 'APPLIED' && cr.entity_id && (
        <Button mt="sm" size="xs" variant="light" leftSection={<IconEye size={14} />}
          onClick={() => documentsApi.view(cr.entity_id as string)}>
          View the file (opens a new tab)
        </Button>
      )}
      {gone && (
        <Alert mt="sm" color="gray" variant="light">
          The file was deleted when this upload was {cr.status === 'REJECTED' ? 'rejected' : 'cancelled'}.
          Its name, size and SHA-256 fingerprint stay on this request as the record.
        </Alert>
      )}
    </>
  );
}
